import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import AppointmentApiInvoker from '../../../../../api/AppointmentApiInvoker'
import { getTransferTypeId, getTransferTypes, TransferType } from '../constants'
import {
  FormState,
  Hospital,
  RecurrenceObject,
  EditTransferServiceHookProps,
} from '../interfaces'
import {
  isAlternativeTransfer,
  isStandardTransfer,
} from '../utils/editTransferServiceUtils'
import { useDriversData } from './useDriversData'
import { TransferItemRequest } from '../../../interfaces'
import { calculateArrivalPoints } from '../../../utils/calculateArrivalPoints'
import { TRANSFER_APPOINTMENT_WAYS } from '../../../utils/constants'

export const useEditTransferService = ({
  itemData,
  itemSelect,
  customer,
  onSave,
}: EditTransferServiceHookProps) => {
  const { t } = useTranslation()

  const transferTypes = getTransferTypes(t)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [openDriverModal, setOpenDriverModal] = useState<boolean>(false)
  const [form, setForm] = useState<FormState>({
    transferType: null,
    hospital: null,
    time_end_suggested: '',
    distance_suggested: '',
    recurrence: null,
    hospitals: [],
    startTime: '',
    startDate: null,
    startLatitude: '',
    startLongitude: '',
    transferScheduleStatus: '',
    destinationLatitude: "",
    destinationLongitude: "",
    driver: null,
    itemIndex: -1,
  })

  const alternativeTransfer = isAlternativeTransfer(itemData)
  const standardTransfer = isStandardTransfer(itemData)

  const {
    transferType,
    hospital,
    time_end_suggested,
    distance_suggested,
    recurrence,
    hospitals,
    startTime,
    startDate,
    startLatitude,
    startLongitude,
    driver,
    destinationLatitude,
    destinationLongitude
  } = form

  const {
    onSaveDriver,
    handleDriversModal,
    canAssignDriver,
    formatDriverData,
  } = useDriversData({
    itemData,
    setForm,
    setOpenDriverModal,
    itemSelect,
  })

  const handleValue = (
    key: string,
    value: string | number | boolean | RecurrenceObject
  ) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleDirectionsCalculated = (data: {
    distance: string
    time: string
  }) => {
    setForm((prev) => ({
      ...prev,
      distance_suggested: data.distance,
      time_end_suggested: data.time,
    }))
  }

  const getSelectedItem = () => {
    if (alternativeTransfer) {
      return itemData.items.find(
        (obj: Partial<TransferItemRequest>) => obj.order === itemSelect
      )
    }
    if (standardTransfer) {
      return itemData.recurrence.items.find(
        (obj: TransferItemRequest) => obj.transferItemRequestId === itemSelect
      )
    }
    return undefined
  }

  const getTransferType = (
    selectedItem: Partial<TransferItemRequest> | undefined
  ) => {
    if (alternativeTransfer) {
      return getTransferTypeId(
        t,
        selectedItem?.transferType ?? TransferType.STANDARD
      )
    }
    if (standardTransfer) {
      return getTransferTypeId(t, itemData?.serviceName)
    }
    return getTransferTypeId(t, TransferType.STANDARD)
  }

  const parseTrasladoObj = () => {
    const { itemIndex } = form
    if (standardTransfer) {
      const {
        endType,
        companyIdPartnership,
        processingScheduleType,
        transferItemRequestStatus,
        order,
        transferWay,
      } = itemData.recurrence.items[itemIndex]
      return {
        transferItemRequestId: itemSelect,
        recurrenceType: form?.recurrence?.recurrenceType,
        endType: endType,
        companyIdPartnership: companyIdPartnership,
        driverId: form?.driver?.id ? { personId: form.driver.id } : null,
        processingScheduleType: processingScheduleType,
        transferItemRequestStatus: transferItemRequestStatus,
        startTime: form.startTime,
        order: order,
        every: form?.recurrence?.every,
        monthDay: form?.recurrence?.monthRecurrence,
        weekDays: form?.recurrence?.weekRecurrence?.join(','),
        startDate: form.startDate,
        transferType: form.transferType,
        hospital: form.hospital,
        startDateRoundTrip: form.startDate,
        startTimeRoundTrip: form.startTime,
        transferWay: transferWay,
      }
    }
  }

  const saveService = async () => {
    setIsLoading(true)
    if (standardTransfer) {
      const putObj = parseTrasladoObj()
      await new Promise((res, rej) => {
        AppointmentApiInvoker.postEditTransferAppointment(
          itemSelect,
          putObj,
          (data: any) => {
            onSave()
            res(data)
          },
          (error: Error) => {
            setIsLoading(false)
            rej(error)
          }
        )
      })
    } else if (alternativeTransfer) {
      onSave(form)
    }
    setIsLoading(false)
  }

  const initializeValues = async () => {
    const hospitals = await new Promise<Hospital[]>((resolve, reject) => {
      AppointmentApiInvoker.getHospital(
        (res: Hospital[]) => resolve(res),
        (error: Error) => reject(error)
      )
    })

    const selectedItem = getSelectedItem()
    if (!selectedItem) {
      setIsLoading(false)
      return
    }

    const transferType = getTransferType(selectedItem)
    const hospital = hospitals.find(
      (obj: Hospital) => obj.hospitalId === selectedItem?.hospital?.hospitalId
    )

    const driver = formatDriverData(selectedItem.driver ?? null)

    const { startPoint, destinationPoint } = calculateArrivalPoints({
      transferWay: selectedItem.transferWay as TRANSFER_APPOINTMENT_WAYS,
      clientDirections: {
        latitude: customer.latitude,
        longitude: customer.longitude,
      },
      hospitalDirections: {
        latitude: hospital?.latitude ?? '',
        longitude: hospital?.longitude ?? '',
      },
    })

    if (hospital) {
      const formData = {
        transferType,
        hospital,
        recurrence: {
          recurrenceType: selectedItem.recurrenceType ?? '',
          every: selectedItem.every ?? 0,
          weekRecurrence: selectedItem.weekDays
            ? selectedItem.weekDays.split(',')
            : [],
          monthRecurrence: String(selectedItem.monthDay ?? ''),
        },
        hospitals,
        startTime: selectedItem.startTime ?? null,
        startDate: selectedItem.startDate ?? null,
        startLatitude: startPoint.latitude,
        startLongitude: startPoint.longitude,
        destinationLatitude: destinationPoint.latitude,
        destinationLongitude: destinationPoint.longitude,
        driver,
        itemIndex: isAlternativeTransfer(itemData)
          ? itemData.items.findIndex(
              (obj: Partial<TransferItemRequest>) => obj.order === itemSelect
            )
          : itemData.recurrence.items.findIndex(
              (obj: TransferItemRequest) =>
                obj.transferItemRequestId === itemSelect
            ),
      }
      setForm((prevState) => ({ ...prevState, ...formData }))
    }
    setIsLoading(false)
  }

  useEffect(() => {
    initializeValues()
  }, [])

  return {
    isLoading,
    transferTypes,
    transferType,
    handleValue,
    hospitals,
    hospital,
    startDate,
    startTime,
    recurrence,
    distance_suggested,
    time_end_suggested,
    startLatitude,
    startLongitude,
    handleDirectionsCalculated,
    canAssignDriver,
    handleDriversModal,
    saveService,
    openDriverModal,
    onSaveDriver,
    driver,
    destinationLatitude,
    destinationLongitude
  }
}
